import { FormField } from '../../../types/form-builder.types';
import { useEffect, useState } from 'react';
import { cn, useLanguage } from '@shondalai/ui-commons';
import { Clock, AlertTriangle } from 'lucide-react';

interface TimerFieldProps {
  field: FormField;
  value: any;
  onChange: (value: any) => void;
}

export function TimerField({ field, value: _value, onChange: _onChange }: TimerFieldProps) {
  const config = field.config;
  const { t } = useLanguage();
  const timeLimit = (config.timeLimit || 10) * 60; // Convert minutes to seconds
  const warningThreshold = (config.warningThreshold || 2) * 60;
  const [timeRemaining, setTimeRemaining] = useState(timeLimit);
  const [isWarning, setIsWarning] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        const newTime = prev - 1;
        
        if (newTime <= warningThreshold && !isWarning) {
          setIsWarning(true);
        }
        
        if (newTime <= 0) {
          clearInterval(timer);
          // Auto-submit if configured
          if (config.autoSubmit) {
            // Trigger form submission through context
            // This would need to be implemented
          }
          return 0;
        }
        
        return newTime;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [config.autoSubmit, isWarning, warningThreshold]);

  const minutes = Math.floor(timeRemaining / 60);
  const seconds = timeRemaining % 60;
  const percentage = (timeRemaining / timeLimit) * 100;

  return (
    <div
      className={cn("p-4 rounded transition-all duration-200")}
      style={{
        border: isWarning
          ? '1px solid var(--ef-error, #EF4444)'
          : '1px solid var(--ef-border, #E2E8F0)',
        background: isWarning
          ? 'rgba(239, 68, 68, 0.05)'
          : 'var(--ef-surface, #F8FAFC)'
      }}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {isWarning ? (
            <AlertTriangle
              className="w-4 h-4"
              strokeWidth={1.5}
              style={{ color: 'var(--ef-error, #EF4444)' }}
            />
          ) : (
            <Clock
              className="w-4 h-4"
              strokeWidth={1.5}
              style={{ color: 'var(--ef-textSecondary, #64748B)' }}
            />
          )}
          <span
            className="text-sm font-medium"
            style={{ color: 'var(--ef-text, #1E293B)' }}
          >
            {config.label || t('COM_EASYFORMS_TIME_REMAINING', 'Time Remaining')}
          </span>
        </div>
        <span
          className="text-2xl font-semibold tabular-nums tracking-tight"
          style={{
            color: isWarning
              ? 'var(--ef-error, #EF4444)'
              : 'var(--ef-text, #1E293B)'
          }}
        >
          {String(minutes).padStart(2, '0')}:{String(seconds).padStart(2, '0')}
        </span>
      </div>
      
      {/* Progress Bar */}
      <div
        className="h-1.5 rounded-full overflow-hidden"
        style={{ background: 'var(--ef-border, #E2E8F0)', opacity: 0.3 }}
      >
        <div
          style={{
            width: `${percentage}%`,
            background: isWarning
              ? 'var(--ef-error, #EF4444)'
              : 'var(--ef-accent, #3B82F6)',
            transition: 'all 1s linear'
          }}
          className="h-full rounded-full"
        />
      </div>

      {/* Warning Message */}
      {isWarning && (
        <p
          className="text-xs font-light mt-2"
          style={{ color: 'var(--ef-error, #EF4444)', opacity: 0.7 }}
        >
          {t('COM_EASYFORMS_TIME_IS_RUNNING_OUT', 'Time is running out!')}
        </p>
      )}
    </div>
  );
}
